import { isSharedLoginDomain } from "@/utils/local-domain";
import { removeItem } from "@/services/storage-data";

/**
 * Branding cached for the print house we are leaving. Dropping it keeps a same-origin switch
 * from painting the previous print house's brand: the saved theme is applied on mount, long
 * before validate reports the theme of the print house we are switching into.
 */
const clearCachedPrintHouseBranding = (): void => {
  removeItem("selectedTheme");
  removeItem("systemLogo");
};

interface RedirectToPrintHouseParams {
  /** The target print house's primary domain (e.g. pix.gomake.net). */
  domain?: string;
  token: string;
  refreshToken?: string;
  /** Path to land on when staying on the current origin. */
  fallbackPath?: string;
  /** Path to land on after the redirect (used both for same-origin and cross-domain handoff). */
  returnPath?: string;
  /**
   * When true, always hand off to `domain` if it differs from the current host — even on a
   * shared login host. Used after login when the user came from a specific print-house domain.
   */
  forceDomainRedirect?: boolean;
}

/**
 * Sends the browser to the selected print house, landing on `returnPath` when provided.
 *
 * - On localhost, or when the target domain matches the current host, we stay on the same origin
 *   and do a full reload so the session is re-validated against the selected print house.
 * - On a shared login host (e.g. gomake.net) we stay put too, unless `forceDomainRedirect` asks
 *   us to hop to a specific print-house domain the user originally came from.
 * - Otherwise we hand the tokens off to the print house's own domain via `/token-auth`.
 */
export const redirectToPrintHouse = ({
  domain,
  token,
  refreshToken,
  fallbackPath = "/home",
  returnPath,
  forceDomainRedirect = false,
}: RedirectToPrintHouseParams): void => {
  if (typeof window === "undefined") return;

  const landing =
    returnPath && returnPath.startsWith("/") ? returnPath : fallbackPath;
  const currentHost = window.location.host;
  const currentHostname = window.location.hostname;
  const isLocalhost =
    currentHostname === "localhost" ||
    currentHostname === "127.0.0.1" ||
    currentHostname === "::1";
  const sameDomain =
    !domain ||
    domain === currentHost ||
    currentHost.split(":")[0] === domain.split(":")[0];

  clearCachedPrintHouseBranding();

  if (isLocalhost || sameDomain) {
    // Token is already in localStorage on this origin; reload to re-run validation.
    window.location.assign(landing);
    return;
  }

  // Shared login hosts normally stay put (single-domain experience), unless we were explicitly
  // asked to return the user to the specific print-house domain they started on.
  if (!forceDomainRedirect && isSharedLoginDomain(currentHostname)) {
    window.location.assign(landing);
    return;
  }

  // Pass the tokens in the URL FRAGMENT (`#…`), never the query string. Access/refresh JWTs are
  // large (the refresh token is an even larger JWE), and in production the print-house domains sit
  // behind a WAF/load balancer that rejects such long, base64-heavy query strings with 403 Forbidden.
  // The fragment is never sent to the server, so it bypasses the WAF entirely and also keeps the
  // tokens out of proxy/access logs and browser history on the server side.
  const params = new URLSearchParams();
  params.set("token", token);
  if (refreshToken) params.set("refreshToken", refreshToken);
  if (landing && landing.startsWith("/")) params.set("redirect", landing);
  window.location.href = `https://${domain}/token-auth#${params.toString()}`;
};
